import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Phone, Mail, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatBot from '../components/ChatBot';
import { Toaster } from '../components/ui/sonner';
import { localServicePages, LOCAL_PHONE, LOCAL_PHONE_TEL, LOCAL_EMAIL } from '../data/localPages';

export default function LocalServicePage() {
  const { slug } = useParams();
  const page = localServicePages.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (page) {
      document.title = page.title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', page.metaDescription);
    }
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`local-page-${page.slug}`}>
      <Header />
      <main>
        <section className="pt-32 sm:pt-40 pb-14 bg-gray-900 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--font-serif)' }} data-testid="local-page-h1">
              {page.h1}
            </h1>
            <p className="text-base md:text-lg text-gray-200 max-w-3xl">{page.intro}</p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-12">
          <article className="lg:col-span-2 space-y-6 text-gray-700 leading-relaxed text-base md:text-lg" data-testid="local-page-body">
            {page.paragraphs.map((text, i) => (
              <p key={i}>{text}</p>
            ))}
          </article>
          <aside className="space-y-8">
            <div className="rounded-2xl border border-gray-200 bg-orange-50 p-6">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4">What we provide</h2>
              <ul className="space-y-3">
                {page.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#D97904' }} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-gray-900 text-white p-6" data-testid="local-page-cta">
              <h2 className="text-base md:text-lg font-bold mb-3">{page.ctaHeading}</h2>
              <p className="text-gray-300 text-sm mb-5">
                Contact iWurks360 for a free consultation. Pricing is provided as a custom quote after we understand your needs.
              </p>
              <a href={`tel:${LOCAL_PHONE_TEL}`} className="flex items-center gap-3 font-semibold mb-3 hover:opacity-90" data-testid="local-page-phone">
                <Phone className="w-5 h-5" style={{ color: '#FF9900' }} /> {LOCAL_PHONE}
              </a>
              <a href={`mailto:${LOCAL_EMAIL}`} className="flex items-center gap-3 font-semibold hover:opacity-90 break-all" data-testid="local-page-email">
                <Mail className="w-5 h-5" style={{ color: '#FF9900' }} /> {LOCAL_EMAIL}
              </a>
              <Link
                to="/"
                state={{ scrollTo: 'contact' }}
                className="mt-6 block text-center font-bold rounded-full px-6 py-3 text-white hover:opacity-90"
                style={{ backgroundColor: '#D97904' }}
                data-testid="local-page-contact-link"
              >
                Request a Consultation
              </Link>
            </div>
          </aside>
        </section>
      </main>
      <Footer />
      <ChatBot />
      <Toaster position="top-right" />
    </div>
  );
}
